import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateSurveyContactHistoryAsync$Json$Params } from '../../../api/fn/survey-contact-history/create-survey-contact-history-async-json';
import {
  ClientSurveyContactModel,
  ClientSurveyContactTypeModel,
} from '../../../api/models';
import {
  SurveyContactService,
  SurveyContactTypeService,
} from '../../../api/services';
import { AccountInfoService } from '../../../services/account-info.service';
import { ToastService } from '../../../services/toast.service';
import { DateTimeUtils } from '../../../utils/datetime.utils';
import { BaseAccountInfoComponent } from '../../BaseAccountInfo/BaseAccountInfo.component';
import { SurveyContactHistoryService } from './../../../api/services/survey-contact-history.service';

@Component({
  selector: 'app-add-contact-history-modal',
  templateUrl: './add-contact-history-modal.component.html',
  styleUrls: ['./add-contact-history-modal.component.scss'],
})
export class AddContactHistoryModalComponent
  extends BaseAccountInfoComponent
  implements OnInit
{
  @Input() surveyTypeId: number;
  @Input() matterId: number;
  @Input() month: number;
  @Input() year: number;

  @Output() reloadParent: EventEmitter<boolean> = new EventEmitter<boolean>();

  contactTypes: ClientSurveyContactTypeModel[] = [];
  surveyContact: ClientSurveyContactModel;

  areSurveyContactTypesLoading = true;
  isAddContactHistoryModalVisible = false;
  hasSurveyContact = false;

  addContactHistoryForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    protected override accountInfoService: AccountInfoService,
    private surveyContactService: SurveyContactService,
    private surveyContactHistoryService: SurveyContactHistoryService,
    private surveyContactTypeService: SurveyContactTypeService
  ) {
    super(accountInfoService);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.addContactHistoryForm = this.fb.group({
      contactType: ['', Validators.required],
      notes: [''],
    });
  }

  onSubmit() {
    if (this.addContactHistoryForm.valid) {
      // Handle form submission
      this.submitContactRecord();
    }
  }

  submitContactRecord(isForSurveySend = false, isForTakenSurvey = false) {
    if (!isForSurveySend && !isForTakenSurvey) {
      this.toggleAddContactHistoryModal();
    }

    if (!this.hasSurveyContact) {
      // Create
      this.surveyContactService
        .createSurveyContactAsync$Json$Response({
          body: {
            surveyTypeId: this.surveyTypeId,
            clientRepId: this.accountInfo!.repId!,
            caseId: this.matterId,
            month: this.month,
            year: this.year,
          },
        })
        .subscribe({
          next: (r) => {
            //this.isSurveyHistoryLoading = false;
            if (!r.body) {
              console.error(r.body);
              this.toastService.showToast(
                'danger',
                'Problem adding contact record'
              );
            } else {
              // Create survey contact history.
              this.submitContactHistoryRecordAfter(
                r.body,
                isForSurveySend,
                isForTakenSurvey
              );
            }
          },
          error: (error) => {
            console.error(error, this);
            this.toastService.showToast(
              'danger',
              'Problem adding contact record'
            );
          },
        });
    } else {
      // Update
      this.surveyContactService
        .updateSurveyContactAsync$Json$Response({
          surveyContactId: this.surveyContact.surveyContactId!.toString(),
          body: {
            clientRepId: this.surveyContact.clientRepId!,
            note: this.surveyContact.note,
            surveyContactId: this.surveyContact.surveyContactId!,
            month: this.month,
            year: this.year,
          },
        })
        .subscribe({
          next: (r) => {
            //this.isSurveyHistoryLoading = false;
            if (!r.body) {
              console.error(r.body);
              this.toastService.showToast(
                'danger',
                'Problem adding contact record'
              );
            } else {
              // Create survey contact history.
              this.submitContactHistoryRecordAfter(
                r.body,
                isForSurveySend,
                isForTakenSurvey
              );
            }
          },
          error: (error) => {
            console.error(error, this);
            this.toastService.showToast(
              'danger',
              'Problem adding contact history record'
            );
          },
        });
    }
  }

  submitContactHistoryRecordAfter(
    m: ClientSurveyContactModel,
    isForSurveySend = false,
    isForTakenSurvey = false
  ) {
    let params: CreateSurveyContactHistoryAsync$Json$Params = {
      body: {
        clientRepId: this.accountInfo!.repId!,
        contactDate: DateTimeUtils.getCurrentDateTimeFormattedForApi(),
        surveyContactId: m.surveyContactId!,
        // 4 is Emailed
        surveyContactTypeId: isForSurveySend
          ? 10
          : this.addContactHistoryForm.value.contactType,
      },
    };
    if (isForTakenSurvey) {
      params.body!.surveyContactTypeId = 8; // 8 is survey completed
    }
    this.surveyContactHistoryService
      .createSurveyContactHistoryAsync$Json$Response(params)
      .subscribe({
        next: (r) => {
          //this.isSurveyHistoryLoading = false;
          if (!r.body) {
            console.error(r.body);
            this.toastService.showToast(
              'danger',
              'Problem adding contact history record'
            );
          } else {
            this.reloadParent.emit(true); // Notify the parent so they can reload data.
          }
        },
        error: (error) => {
          console.error(error, this);
          this.toastService.showToast(
            'danger',
            'Problem adding contact history record'
          );
        },
      });
  }

  getSurveyContact() {
    return this.surveyContactService.getSurveyContactAsync$Json$Response({
      repId: this.accountInfo?.isAdmin ? undefined : this.accountInfo!.repId!,
      surveyTypeId: this.surveyTypeId,
      caseId: this.matterId,
      month: this.month,
      year: this.year,
    });
  }

  toggleAddContactHistoryModal() {
    if (!this.isAddContactHistoryModalVisible) {
      // Only load contacts if the modal is being opened.
      this.getSurveyContact().subscribe({
        next: (r) => {
          if (!r.body) {
            this.hasSurveyContact = false;
          } else {
            this.hasSurveyContact = true;
            this.surveyContact = r.body;
          }
        },
        error: (error) => {
          console.error(error, this);
          this.toastService.showToast(
            'danger',
            'Problem loading survey contact'
          );
          this.hasSurveyContact = false;
        },
      });
    }

    this.isAddContactHistoryModalVisible =
      !this.isAddContactHistoryModalVisible;

    if (this.contactTypes.length === 0) {
      // Load contact types.
      this.getSurveyContactTypes();
    }
  }

  getSurveyContactTypes() {
    this.areSurveyContactTypesLoading = true;

    this.surveyContactTypeService
      .getSurveyContactTypes$Json$Response()
      .subscribe({
        next: (r) => {
          this.areSurveyContactTypesLoading = false;
          if (!r.body) {
            console.error(r.body);
            return;
          } else {
            this.contactTypes = r.body;
            // Update the default value for contactType after loading contact types
            if (this.contactTypes.length > 0) {
              this.addContactHistoryForm.patchValue({
                contactType: this.contactTypes[0].surveyContactTypeId,
              });
            }
          }
        },
        error: (error) => {
          console.error(error, this);
          this.areSurveyContactTypesLoading = false;
          this.toastService.showToast(
            'danger',
            'Problem loading survey contact types'
          );
        },
      });
  }
}
